import BaseTable, { FieldType } from "../table/BaseTable.tsx";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { entity } from "@/store/modules/entity.ts";
import NewAttributeModal from "../modal/NewAttributeModal.tsx";
import { useNavigate } from "react-router-dom";
import NewEntityModal from "../modal/NewEntityModal.tsx";
import EntityBulkUploadModal from "../modal/EntityBulkUploadModal.tsx";
import { ReadEntity } from "@/model/response/entity/ReadEntityResponse.ts";
import { attribute } from "@/store/modules/attribute.ts";
import { deploy } from "@/store/modules/deploy.ts";
import { RotatingLines, ThreeDots } from "react-loader-spinner";
import copy from "copy-to-clipboard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import SuccessDeployModal from "@/components/modal/SuccessDeployModal.tsx";
import NavBarHeading from "@/components/menu/NavBarHeading.tsx";
import { StoreUtil } from "@/util/store/StoreUtil.ts";
import { BaseResponse } from "@/model/response/BaseResponse.ts";
import { notification } from "@/store/modules/notification.ts";
import axios, { CancelTokenSource } from "axios";
import { base } from "@/store/modules/base.ts";
import { ModalConstant } from "@/util/constant/ModalConstant.ts";
import { RouteConstant } from "@/util/constant/RouteConstant.ts";
import { useFirstRender } from "@/util/useFirstRender.tsx";
import { generateCode } from "@/store/modules/generateCode.ts";
import { toast } from "react-toastify";
import DeployModal from "../modal/DeployModal.tsx";
import { PayloadAction } from "@reduxjs/toolkit";
import { CheckStatusResponse } from "@/model/response/generateCode/CheckStatusResponse.ts";
import { BaseService } from "@/service/BaseService.ts";

export default function EntityPage() {
  const [modalVisible, setModalVisible] = useState(false);
  
  const applicationState = useSelector((state: RootState) => state.application);
  const generateCodeState = useSelector(
    (state: RootState) => state.generateCode
  );
  const deployState = useSelector((state: RootState) => state.deploy);
  const [showModal, setShowModal] = useState(false);
  const [show, setShow] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [isDownloading, setIsDownloading] = useState(false);
  const [entityData, setEntityData] = useState<Partial<ReadEntity> | null>({});
  
  // New state for Java timeout
  const [isJavaGenerating, setIsJavaGenerating] = useState(false);
  const [javaTimeoutId, setJavaTimeoutId] = useState<NodeJS.Timeout | null>(null);
  
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const entityState = useSelector((state: RootState) => state.entity);
  const currentApplicationId =
    applicationState?.selectedApplication?.applicationId;

  const workspaceState = useSelector((state: RootState) => state.workspace);
  const isFirstRender = useFirstRender();
  const authState = useSelector((state: RootState) => state.auth);
  const [cancelToken, setCancelToken] = useState<CancelTokenSource | null>(
    null
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (javaTimeoutId) {
        clearTimeout(javaTimeoutId);
      }
    };
  }, [javaTimeoutId]);

  const refreshPage = () => {
    window.location.reload();
  };

  const cancelRequest = () => {
    if (cancelToken) {
      console.log(cancelToken);
      cancelToken.cancel("Request was canceled");
    }
    dispatch(deploy.mutation.setLoading(false));
    toast.warn("Request Canceled", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const disabledLink = {
    backgroundColor: "lightgray",
    textDecoration: "none",
    cursor: "not-allowed",
  };

  const handleCopy = () => {
    const textToCopy = deployState.swaggerUrl;
    copy(textToCopy);
  };

  const showSuccessMessage = (response: BaseResponse) => {
    StoreUtil.execute(() => {
      dispatch(
        notification.mutation.openModal({
          type: "success",
          title: "Success",
          body: response.responseMessage,
        })
      );
    });
  };

  const downloadKotlinCode = async () => {
    const isJava =
      applicationState.selectedApplication.applicationLanguage === "Java";
    const urls =
      isJava && currentS3Url
        ? [currentS3Url, currentDatabaseUrl]
        : [
            `${import.meta.env.VITE_DOWNLOAD_BASE_URL}${Number(
              currentApplicationId
            )}`,
          ];
    const x = `${import.meta.env.VITE_DOWNLOAD_BASE_URL}${Number(
      currentApplicationId
    )}`;
    console.log("xx: ", x);

    for (const url of urls) {
      const link = document.createElement("a");
      link.href = url;
      link.download = "";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    console.log(urls);
  };

  const downloadCode = async () => {
    console.log(
      "applicationState.selectedApplication.applicationLanguage ",
      applicationState.selectedApplication.applicationLanguage
    );
    const isJava =
      applicationState.selectedApplication.applicationLanguage === "Java";
    if (!isJava) return downloadKotlinCode();
    try {
      setIsDownloading(true);
      const response = (await dispatch(
        generateCode.action.checkStatus(
          applicationState.selectedApplication.applicationId
        )
      )) as PayloadAction<CheckStatusResponse>;

      console.log("response", response);

      if (
        response?.payload?.applicationCodeStatus?.toLocaleLowerCase() ==
        "completed"
      ) {
        const link = document.createElement("a");
        const x = `${import.meta.env.VITE_DOWNLOAD_BASE_URL}${Number(
          currentApplicationId
        )}`;
        const url = response?.payload?.applicationS3Url;
        link.href = isJava ? url : x;
        console.log("url: ", isJava ? url : x);

        link.download = "";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const onGenerateCode = async () => {
    console.log(
      "applicationID: ",
      applicationState.selectedApplication.applicationId
    );
    const isJava =
      applicationState.selectedApplication.applicationLanguage === "Java";
    
    // Set Java generating state if it's Java
    if (isJava) {
      setIsJavaGenerating(true);
      
      // Clear any existing timeout
      if (javaTimeoutId) {
        clearTimeout(javaTimeoutId);
      }
      
      // Set 6-minute timeout for Java
      const timeoutId = setTimeout(() => {
        setIsJavaGenerating(false);
        dispatch(generateCode.mutation.setLoading(false));
        toast.success("Java Code Generation Complete! You can now download.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }, 6 * 60 * 1000); // 6 minutes in milliseconds
      
      setJavaTimeoutId(timeoutId);
    }

    await dispatch(
      isJava
        ? generateCode.action.generate(
            applicationState.selectedApplication.applicationId
          )
        : generateCode.action.generateKotlin(
            applicationState.selectedApplication.applicationId
          )
    );

    dispatch(generateCode.mutation.setShowDownload(true));

    // For non-Java, keep the original 18-second timeout
    if (!isJava) {
      dispatch(
        setTimeout(() => {
          generateCode.mutation.setLoading(false);
        }, 18000)
      );
      
      await dispatch(
        generateCode.action.checkStatus(
          applicationState.selectedApplication.applicationId
        )
      );
      
      toast.success("Code Generated Successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleOnClick = (data: ReadEntity) => {
    dispatch(entity.mutation.setSelectedEntity(data));
    dispatch(attribute.mutation.setAttributeToEmpty());
    navigate(RouteConstant.dashboard.attribute.path);
  };
  
  const fields: FieldType[] = [
    {
      label: "Entity Name",
      key: "entityName",
      onClick: (data) => handleOnClick(data),
    },
    { label: "Description", key: "entityDescription" },
    { label: "Status", key: "entityStatus" },
    { label: "Created At", key: "entityCreatedAt" },
  ];

  const deployApplication = (applicationId: number) => {
    const tokenSource = axios.CancelToken.source();
    setCancelToken(tokenSource);
    dispatch(
      deploy.action.create({
        data: applicationId,
        onSuccess: (response: BaseResponse) => {
          showSuccessMessage(response);
        },
      })
    );
  };

  const items = useMemo(() => {
    if (isFirstRender) {
      dispatch(
        entity.action.readByEntityApplicationId({
          entityApplicationId:
            applicationState?.selectedApplication?.applicationId || 0,
        })
      );
      return [];
    }
    return entityState.entityById;
  }, [entityState.entityById]);
  
  const currentS3Url =
    generateCodeState.applicationS3Urls[
      applicationState?.selectedApplication?.applicationId || 1
    ];
  
  const currentDatabaseUrl =
    generateCodeState.applicationDatabaseUrls?.[
      applicationState?.selectedApplication?.applicationId || 1
    ];

  // Check if download should be disabled
  const isJava = applicationState.selectedApplication.applicationLanguage === "Java";
  const shouldDisableDownload = isJava 
    ? isJavaGenerating || !(!!currentS3Url || generateCodeState.showDownload)
    : !(!!currentS3Url || generateCodeState.showDownload);

  return (
    <>
      <section
        className="app-content-dashboard"
        style={{ backgroundColor: "white" }}
      >
        <div className="title-wrapper">
          <div
            className="margin-bottom"
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <h5
              className={"h5-header"}
            >{`${workspaceState.selectedWorkspace?.workspaceName.toUpperCase()} > ${applicationState?.selectedApplication?.applicationName.toUpperCase()}`}</h5>
            {deployState.swaggerUrl && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: "1rem",
                }}
              >
                <p style={{ fontWeight: "bold" }}>Swagger URL: </p>
                <p
                  className={
                    deployState.swaggerUrl ? "deploy-text-container" : ""
                  }
                >
                  {deployState.selectedAppId == currentApplicationId &&
                    `${deployState.swaggerUrl}`}
                </p>
                <button onClick={handleCopy} id="copyBtn">
                  <FontAwesomeIcon icon={faCopy} /> Copy
                </button>
              </div>
            )}
          </div>
          <NavBarHeading />
        </div>
        <section className="project-table-wrapper">
          <div className="table-actions-wrapper">
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                onClick={() => {
                  setOpenModal(true);
                  setEntityData(null);
                }}
                className="button"
              >
                Create New Entity
              </button>
              <button
                onClick={() =>
                  dispatch(
                    base.mutation.setModalOptions({
                      show: true,
                      component: ModalConstant.authConfig,
                    })
                  )
                }
                className="button is-secondary"
              >
                Create Auth Config
              </button>
              <button
                onClick={() => {
                  deployApplication(
                    currentApplicationId ? currentApplicationId : 0
                  );
                  dispatch(
                    deploy.mutation.setSelectedAppId(currentApplicationId)
                  );
                  dispatch(deploy.mutation.setSwaggerUrl(""));
                }}
                className="button is-secondary"
              >
                Deploy Codes
              </button>
              <button
                className="button is-secondary"
                onClick={onGenerateCode}
                disabled={generateCodeState.loading || isJavaGenerating}
              >
                {generateCodeState.loading || isJavaGenerating ? (
                  <div
                    style={{
                      display: "flex",
                      gap: ".5rem",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "1rem",
                    }}
                  >
                    <span>
                      {isJavaGenerating ? "Generating Java Code" : "Generating Code"}
                    </span>
                    <ThreeDots color="" width="1.5rem" radius={"1.5rem"} />
                  </div>
                ) : (
                  "Generate Code"
                )}
              </button>
              <button
                title={
                  isJavaGenerating 
                    ? "Java code is still generating. Please wait..." 
                    : "Generate your code to download"
                }
                onClick={downloadCode}
                className="button is-secondary"
                disabled={shouldDisableDownload}
                style={
                  shouldDisableDownload ? disabledLink : {}
                }
              >
                Download Code
              </button>
              <button
                title={"Generate your code to download"}
                onClick={() => setModalVisible(!modalVisible)}
                className="button is-secondary"
                disabled={!!currentS3Url}
              >
                Deploy code to github
              </button>

              {openModal && (
                <NewEntityModal
                  show={openModal}
                  onClose={() => setOpenModal(false)}
                  data={entityData}
                />
              )}
            </div>
            <a
              onClick={() => setShow(true)}
              className="button"
              style={{
                color: "white",
                backgroundColor: "#252f3d",
                border: "none",
                cursor: "pointer",
              }}
            >
              Excel Upload
            </a>
            <EntityBulkUploadModal onClose={() => setShow(false)} show={show} />
          </div>
          <div style={{ width: "100%", marginLeft: "auto" }}>
            <BaseTable
              title={"Entity"}
              component={""}
              items={items ?? []}
              refreshPage={refreshPage}
              loading={entityState.loading}
              showToast={false}
              loader={entityState.loading}
              fields={fields}
              actions={{ view: true }}
              rowOptions={[
                {
                  optionName: "Create Attribute",
                  onClick: (data) => {
                    dispatch(entity.mutation.setSelectedEntity(data));
                    setShowModal(true);
                  },
                  optionStyle: { fontSize: 12 },
                },
                {
                  optionName: "View Entity",
                  onClick: (data: ReadEntity) => handleOnClick(data),
                  optionStyle: { fontSize: 12 },
                },
                {
                  optionName: "Edit Entity",
                  onClick: (data) => {
                    dispatch(entity.mutation.setSelectedEntity(data));
                    setEntityData(data);
                    setOpenModal(true);
                  },
                  optionStyle: { fontSize: 12 },
                },
                {
                  optionName: "Delete Entity",
                  onClick: (data) => {
                    dispatch(entity.mutation.setSelectedEntity(data));
                    dispatch(
                      base.mutation.setModalOptions({
                        show: true,
                        component: ModalConstant.deleteEntity,
                      })
                    );
                  },
                  optionStyle: { fontSize: 12 },
                },
              ]}
            />
            {deployState.loading && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 9999,
                  height: "100vh",
                }}
                className={"modal1_background-overlay"}
              >
                <div
                  style={{
                    backgroundColor: "white",
                    padding: "20px 80px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <RotatingLines
                    visible={true}
                    strokeColor={"#750505"}
                    width={"4rem"}
                  />
                  <p>Deploying...</p>
                  <button
                    onClick={cancelRequest}
                    style={{
                      display: "flex",
                      borderRadius: "8px",
                      justifyContent: "center",
                    }}
                    className="button is-secondary w-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {showModal && (
              <NewAttributeModal
                show={showModal}
                onClose={() => setShowModal(false)}
              />
            )}
            <SuccessDeployModal
              show={deployState.deployModal}
              onClose={() => dispatch(deploy.mutation.setDeployModal(false))}
            />
          </div>
        </section>
        {modalVisible && (
          <DeployModal
            show={modalVisible}
            onClose={() => setModalVisible(false)}
          />
        )}
      </section>
    </>
  );
}